import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ShoppingBag, Clock, ChefHat, CheckCircle2, XCircle, Loader2, RefreshCw,
  UtensilsCrossed, Package, Bell, Volume2, VolumeX
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Bell, next: 'confirmed', nextLabel: 'Confirmer' },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2, next: 'preparing', nextLabel: 'En préparation' },
  preparing: { label: 'En préparation', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: ChefHat, next: 'ready', nextLabel: 'Prête' },
  ready: { label: 'Prête', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Package, next: 'served', nextLabel: 'Servie' },
  served: { label: 'Servie', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: CheckCircle2, next: null, nextLabel: null },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, next: null, nextLabel: null }
};

const OrdersDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, all, served
  const [refreshing, setRefreshing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('zot_orders_sound') !== 'off');
  const soundEnabledRef = useRef(soundEnabled);
  const audioCtxRef = useRef(null);
  const knownIdsRef = useRef(null);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  };

  const playChime = useCallback(() => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === 'suspended') ctx.resume();
      // Double "ding-dong" bell, played twice
      [0, 0.7].forEach(offset => {
        [880, 1174.66].forEach((freq, i) => {
          const t = ctx.currentTime + offset + i * 0.18;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, t);
          gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(t);
          osc.stop(t + 0.7);
        });
      });
    } catch (e) { /* audio unavailable */ }
  }, []);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    soundEnabledRef.current = next;
    localStorage.setItem('zot_orders_sound', next ? 'on' : 'off');
    if (next) {
      playChime();
      toast.success('Alerte sonore activée', { description: 'Un signal retentira à chaque nouvelle commande.' });
    } else {
      toast('Alerte sonore désactivée');
    }
  };

  // Unlock audio context on first user interaction (browser autoplay policy)
  useEffect(() => {
    const unlock = () => {
      try { getAudioCtx().resume(); } catch (e) { /* noop */ }
      document.removeEventListener('click', unlock);
    };
    document.addEventListener('click', unlock);
    return () => document.removeEventListener('click', unlock);
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/orders');
      const data = res.data;
      if (knownIdsRef.current !== null) {
        const newOrders = data.filter(o => !knownIdsRef.current.has(o.id) && o.status === 'pending');
        if (newOrders.length > 0) {
          if (soundEnabledRef.current) playChime();
          toast.success(
            `${newOrders.length} nouvelle${newOrders.length > 1 ? 's' : ''} commande${newOrders.length > 1 ? 's' : ''} !`,
            { description: newOrders.map(o => o.order_number).join(', '), duration: 8000 }
          );
        }
      }
      knownIdsRef.current = new Set(data.map(o => o.id));
      setOrders(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  }, [playChime]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh every 15s
  useEffect(() => {
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success(`Commande ${STATUS_CONFIG[status]?.label?.toLowerCase()}`);
      fetchOrders();
    } catch (e) { toast.error('Erreur'); }
  };

  const filteredOrders = orders.filter(o => {
    if (filter === 'active') return !['served', 'cancelled'].includes(o.status);
    if (filter === 'served') return o.status === 'served';
    return true;
  });

  const pendingCount = orders.filter(o => o.status === 'pending').length;

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="flex items-center justify-center min-h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6" data-testid="orders-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingBag className="h-8 w-8 text-orange-500" /> Commandes
            {pendingCount > 0 && (
              <Badge className="bg-red-500 text-white animate-pulse text-sm">{pendingCount} nouvelle{pendingCount > 1 ? 's' : ''}</Badge>
            )}
          </h1>
          <p className="text-gray-500 mt-1">{orders.length} commande{orders.length > 1 ? 's' : ''} aujourd'hui</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={toggleSound}
            className={`rounded-full ${soundEnabled ? 'border-emerald-300 text-emerald-600 hover:bg-emerald-50' : 'text-gray-400'}`}
            data-testid="sound-toggle-btn"
            title={soundEnabled ? 'Désactiver l\'alerte sonore' : 'Activer l\'alerte sonore'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4 mr-2" /> : <VolumeX className="h-4 w-4 mr-2" />}
            {soundEnabled ? 'Son activé' : 'Son coupé'}
          </Button>
          <Button variant="outline" onClick={() => { setRefreshing(true); fetchOrders(); }} className="rounded-full" data-testid="refresh-orders-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { id: 'active', label: 'En cours' },
          { id: 'all', label: 'Toutes' },
          { id: 'served', label: 'Servies' }
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === f.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`filter-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Orders list */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <ShoppingBag className="h-12 w-12 mx-auto mb-3" />
          <p>Aucune commande</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredOrders.map(order => {
            const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            return (
              <Card key={order.id} className={`border-2 transition-all ${order.status === 'pending' ? 'border-yellow-300 shadow-lg shadow-yellow-100' : 'border-gray-100'}`}
                data-testid={`order-card-${order.id}`}>
                <CardContent className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-lg text-gray-900">{order.order_number}</span>
                      <Badge className={`text-[11px] border ${cfg.color}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />{cfg.label}
                      </Badge>
                    </div>
                    <span className="text-xs text-gray-400">{formatTime(order.created_at)}</span>
                  </div>

                  {/* Table / Type */}
                  <div className="flex items-center gap-2">
                    {order.order_type === 'dine_in' ? (
                      <Badge variant="outline" className="text-xs"><UtensilsCrossed className="h-3 w-3 mr-1" />{order.table_label || 'Sur place'}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs"><Package className="h-3 w-3 mr-1" />A emporter</Badge>
                    )}
                    {order.customer_name && <span className="text-xs text-gray-500">{order.customer_name}</span>}
                  </div>

                  {/* Items */}
                  <div className="space-y-1 bg-gray-50 rounded-lg p-2.5">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-700"><span className="font-medium text-gray-900">{item.quantity}x</span> {item.dish_name}</span>
                        <span className="text-gray-500 font-medium">{(item.unit_price * item.quantity).toFixed(2)} €</span>
                      </div>
                    ))}
                    {order.notes && <p className="text-xs text-orange-600 mt-1 italic">Note: {order.notes}</p>}
                  </div>

                  {/* Total + Actions */}
                  <div className="flex items-center justify-between pt-1">
                    <span className="font-heading font-bold text-lg text-gray-900">{order.total.toFixed(2)} €</span>
                    <div className="flex gap-2">
                      {order.status === 'pending' && (
                        <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 rounded-full text-xs"
                          onClick={() => updateStatus(order.id, 'cancelled')}>
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Refuser
                        </Button>
                      )}
                      {cfg.next && (
                        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full text-xs"
                          onClick={() => updateStatus(order.id, cfg.next)} data-testid={`order-next-${order.id}`}>
                          {cfg.nextLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersDashboard;
